import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import Papa from "papaparse";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material UI components
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import FormControlLabel from "@mui/material/FormControlLabel";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";

// Icons
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ErrorIcon from "@mui/icons-material/Error";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { getSessionStorage } from "../../Helper";
import { API_BASE_URL } from "../../Config";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { useTheme } from "@mui/material";

function PodDownload() {
  const showSidebar = useSelector((state) => state.sidebar.showSidebar);
  const [inputType, setInputType] = useState("inputDate");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [errorModal, setErrorModal] = useState(false);
  const currentDate = new Date().toISOString().split("T")[0];
  const [loadingImage, setLoadingImage] = useState(null);
  const [downloadButton, setDownloadButton] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedCNoteNumber, setSelectedCNoteNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [rotationAngle, setRotationAngle] = useState(0);
  const [loadingZip, setLoadingZip] = useState(false);
  const [maxToDate, setMaxToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const toDateInputRef = useRef(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const obj = {
    cid: getSessionStorage("cid"),
    accode: getSessionStorage("accode"),
    datefrom: fromDate.split("-").reverse().join("/"),
    dateto: toDate.split("-").reverse().join("/"),
  };

  const handleRotate = () => {
    setRotationAngle((prevAngle) => prevAngle + 90);
  };

  const handleResetRotation = () => {
    setRotationAngle(0);
  };

  // const handleDateChange = (e, setDate, isFromDate) => {
  //   const selectedDate = e.target.value;

  //   if (isFromDate) {
  //     setDate(selectedDate);
  //     const oneMonthLater = new Date(selectedDate);
  //     oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
  //     const maxDate = oneMonthLater.toISOString().split("T")[0];
  //     setMaxToDate(maxDate);
  //   } else {
  //     if (selectedDate >= fromDate && selectedDate <= maxToDate) {
  //       setDate(selectedDate);
  //     } else {
  //       console.log('Invalid date selected for "To Date".');
  //     }
  //   }
  // };

  const handleFromDateChange = (e) => {
    const selectedDate = e.target.value;
    setFromDate(selectedDate);

    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 30);
    const maxDate = date.toISOString().split("T")[0];
    setMaxToDate(maxDate);
    setToDate("");
  };

  const handleToDateClick = () => {
    if (fromDate && !toDate) {
      toDateInputRef.current.value = fromDate;
      setTimeout(() => {
        toDateInputRef.current.value = "";
      }, 0);
    }
  };

  const handleToDateChange = (e) => {
    const selectedDate = e.target.value;
    if (selectedDate >= fromDate && selectedDate <= maxToDate) {
      setToDate(selectedDate);
    } else {
      alert("Please select a date within 30 days from From Date");
      setToDate("");
    }
  };

  const handleFilterSubmit = async () => {
    setSelectedRows([]);
    setLoading(true);

    if (inputType === "inputDate") {
      if (fromDate && toDate) {
        if (fromDate <= toDate) {
          try {
            const response = await axios.post(`${API_BASE_URL}/pod/getdata`, obj);
            const data = response.data;

            const filtered = data.filter((item) => {
              const cNoteDate = new Date(item.CNOTEDATE);
              return cNoteDate >= new Date(fromDate) && cNoteDate <= new Date(toDate);
            });

            const sortedData = filtered.sort(
              (a, b) => new Date(a.CNOTEDATE) - new Date(b.CNOTEDATE)
            );

            if (sortedData.length <= 0) {
              Swal.fire({
                icon: "info",
                title: "No Data Found !!",
                showConfirmButton: false,
                timer: 1500,
              });
            }

            const transformedData = sortedData.map((item) => [
              item.CNOTENO,
              item.CNOTEDATE,
              item.ORIGIN,
              item.DESTINATION,
              item.PODFILE,
            ]);

            setFilteredData(transformedData);
            setDownloadButton(true);
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: "Error fetching data",
              text: error.message,
              timer: 930,
              showConfirmButton: false,
            });
          } finally {
            setLoading(false);
          }
        } else {
          setErrorModal(true);
          setTimeout(() => setErrorModal(false), 2000);
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Please select both From Date and To Date",
          timer: 930,
          showConfirmButton: false,
        });
        setLoading(false);
      }
    } else if (inputType === "uploadFile") {
      if (fileUploaded) {
        const file = document.getElementById("fileUploadSection").files[0];
        const reader = new FileReader();
        reader.onload = async (event) => {
          const data = event.target.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (!jsonData[0] || jsonData[0][0] !== "C Note No") {
            setErrorMessage("Follow template for file upload");
            setTimeout(() => setErrorMessage(""), 2000);
            return;
          }

          const cNoteNumbers = jsonData.slice(1).map((row) => row[0].toString());
          const formattedCNoteNumbers = cNoteNumbers.map((cNote) => ({
            "c note no": cNote,
          }));
          setLoading(true);

          const obj2 = {
            cid: sessionStorage.getItem("cid"),
            accode: sessionStorage.getItem("accode"),
            podlist: formattedCNoteNumbers,
          };

          try {
            const response = await axios.post(`${API_BASE_URL}/pod/getPodList`, obj2);
            const datalist = Array.isArray(response.data) ? response.data : [];

            setFilteredData(datalist);
            setDownloadButton(true);
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: "Error fetching Data",
              text: error.message,
              timer: 930,
              showConfirmButton: false,
            });
          } finally {
            setLoading(false);
          }
        };
        reader.readAsBinaryString(file);
      } else {
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "File not uploaded",
          timer: 930,
          showConfirmButton: false,
        });
      }
    }
  };

  const handleInputTypeChange = (type) => {
    setInputType(type);
  };

  const handleDownload = async () => {
    const selectedData = filteredData.filter((_, index) => selectedRows.includes(index));

    const cNoteNumbers = selectedData.map((row) => row[0] || row.CNOTENO);
    setLoadingZip(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/images/download-zip`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cNoteNumbers),
      });

      if (!response.ok) {
        throw new Error("Failed to download the ZIP file");
      }

      const blob = await response.blob();
      saveAs(blob, "POD_Images.zip");
    } catch (error) {
      console.error("Error downloading ZIP file:", error);
    } finally {
      setLoadingZip(false);
    }
  };

  const handleFileUpload = () => {
    setFileUploaded(true);
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([["C Note No"]]);
    XLSX.utils.book_append_sheet(wb, ws, "POD_template");
    const excelBuffer = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    const excelBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(excelBlob, "POD_template.xlsx");
  };

  const handleRowCheckboxChange = (index) => {
    setSelectedRows((prev) => {
      const selectedIndex = prev.indexOf(index);
      if (selectedIndex === -1) {
        return [...prev, index];
      } else {
        return prev.filter((rowIndex) => rowIndex !== index);
      }
    });
  };

  const handleAllRowsCheckboxChange = () => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows([...Array(filteredData.length).keys()]);
    }
  };

  const handleImageClick = (imageName, cnoteNumber) => {
    setLoadingImage(cnoteNumber);

    setTimeout(() => {
      setLoadingImage(null);
      setSelectedImage(`https://www.magixerp.in/ImageView/MAXPACIFIC/POD/${imageName}`);
      setSelectedCNoteNumber(cnoteNumber);
      setModalVisible(true);
    }, 2000);
  };

  const closeModal = () => {
    setSelectedImage("");
    setModalVisible(false);
  };

  const handleDownloadImage = async () => {
    if (selectedImage) {
      const cNoteNumber = selectedImage.split("_")[1].split(".")[0];
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/images/download-image/${cNoteNumber}`,
          {
            responseType: "blob",
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${cNoteNumber}.jpg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        setModalVisible(false);
      } catch (error) {
        console.error("Error downloading the image:", error);
      }
    }
  };

  // Modal style
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    maxWidth: 900,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    outline: "none",
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        className={`mt-[24%] sm:mt-10 md:mt-0 lg:mt-0 2xl:mt-0 h-[80%] ${
          showSidebar
            ? "transition-all transition-slowest ease ml-20 lg:ml-[18%]"
            : "transition-all transition-slowest ease"
        }`}
      >
        <Card sx={{ p: 2, boxShadow: 3, mt: 3 }}>
          <MDBox display="flex" flexWrap="wrap" alignItems="center" mb={3} gap={1}>
            <MDButton
              variant={inputType === "inputDate" ? "contained" : "outlined"}
              color="info"
              startIcon={<DateRangeIcon />}
              onClick={() => handleInputTypeChange("inputDate")}
              sx={{
                borderWidth: inputType === "inputDate" ? 0 : 1,
                borderStyle: "solid",
                borderColor: "info.main",
              }}
            >
              Date
            </MDButton>

            <MDButton
              variant={inputType === "uploadFile" ? "contained" : "outlined"}
              color="info"
              startIcon={<CloudUploadIcon />}
              onClick={() => handleInputTypeChange("uploadFile")}
              sx={{
                borderWidth: inputType === "uploadFile" ? 0 : 1,
                borderStyle: "solid",
                borderColor: "info.main",
              }}
            >
              Upload
            </MDButton>
          </MDBox>

          {inputType === "inputDate" && (
            <MDBox display="flex" flexWrap="wrap" alignItems="center" mb={3}>
              <MDBox width={{ xs: "100%", sm: "30%", lg: "20%" }} mr={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text">
                  From Date <span style={{ color: "red" }}>*</span>
                </MDTypography>
                {/* <MDInput
                  type="date"
                  fullWidth
                  value={fromDate}
                  onChange={(e) => handleDateChange(e, setFromDate, true)}
                  max={currentDate}
                /> */}
                <MDInput
                  type="date"
                  fullWidth
                  value={fromDate}
                  onChange={handleFromDateChange}
                  max={currentDate}
                />
              </MDBox>
              <MDBox width={{ xs: "100%", sm: "30%", lg: "20%" }} mr={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text">
                  To Date <span style={{ color: "red" }}>*</span>
                </MDTypography>
                {/* <MDInput
                  type="date"
                  fullWidth
                  value={toDate}
                  onChange={(e) => handleDateChange(e, setToDate)}
                  max={maxToDate}
                  min={fromDate}
                /> */}
                <MDInput
                  type="date"
                  inputRef={toDateInputRef}
                  value={toDate}
                  onChange={handleToDateChange}
                  onClick={handleToDateClick}
                  min={fromDate}
                  max={maxToDate}
                  fullWidth
                />
              </MDBox>
              <MDBox mt={2}>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleFilterSubmit}
                  startIcon={<DateRangeIcon />}
                  sx={{ mt: 2 }}
                >
                  Show
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          {inputType === "uploadFile" && (
            <MDBox display="flex" flexWrap="wrap" alignItems="center" mb={3}>
              <MDBox width={{ xs: "100%", sm: "30%", lg: "20%" }} mr={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text">
                  Upload File <span style={{ color: "red" }}>*</span>
                </MDTypography>
                <MDInput
                  type="file"
                  fullWidth
                  id="fileUploadSection"
                  onChange={handleFileUpload}
                  accept=".xls,.xlsx"
                />
              </MDBox>
              <MDBox mr={2} mt={2}>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleFilterSubmit}
                  startIcon={<CloudUploadIcon />}
                  sx={{ mt: 2 }}
                >
                  Upload
                </MDButton>
              </MDBox>
              <MDBox mt={2}>
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleDownloadTemplate}
                  startIcon={<FileDownloadIcon />}
                  sx={{ mt: 2 }}
                >
                  Template
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          {loading && (
            <MDBox display="flex" justifyContent="center" p={3}>
              <CircularProgress color="info" />
            </MDBox>
          )}

          {!loading && filteredData.length > 0 && (
            <MDBox>
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table sx={{ backgroundColor: isDark ? "#121212" : "#ffffff" }}>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        checked={selectedRows.length === filteredData.length}
                        onChange={handleAllRowsCheckboxChange}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            color: isDark ? "#ffffff99" : "#000000",
                          },
                          "&.Mui-checked .MuiSvgIcon-root": {
                            color: isDark ? "#90caf9" : "#1976d2",
                          },
                          "&:hover": {
                            backgroundColor: isDark ? "#1e1e1e" : "#f0f0f0",
                            borderRadius: "4px",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>C Note No</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>C Note Date</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Origin</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Destination</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">
                      POD Image
                    </TableCell>
                  </TableRow>

                  <TableBody>
                    {filteredData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Checkbox
                            checked={selectedRows.includes(index)}
                            onChange={() => handleRowCheckboxChange(index)}
                            sx={{
                              "& .MuiSvgIcon-root": {
                                color: isDark ? "#ffffff99" : "#000000",
                              },
                              "&.Mui-checked .MuiSvgIcon-root": {
                                color: isDark ? "#90caf9" : "#1976d2",
                              },
                              "&:hover": {
                                backgroundColor: isDark ? "#1e1e1e" : "#f0f0f0",
                                borderRadius: "4px",
                              },
                            }}
                          />
                        </TableCell>
                        {row.CNOTENO ? (
                          <>
                            <TableCell>{row.CNOTENO}</TableCell>
                            <TableCell>{row.CNOTEDATE}</TableCell>
                            <TableCell>{row.ORIGIN}</TableCell>
                            <TableCell>{row.DESTINATION}</TableCell>
                            <TableCell align="center">
                              <MDButton
                                variant="gradient"
                                color="warning"
                                onClick={() => handleImageClick(row.PODFILE, row.CNOTENO)}
                              >
                                {loadingImage === row.CNOTENO ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </MDButton>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{row[0]}</TableCell>
                            <TableCell>{row[1]}</TableCell>
                            <TableCell>{row[2]}</TableCell>
                            <TableCell>{row[3]}</TableCell>
                            <TableCell align="center">
                              <MDButton
                                variant="gradient"
                                color="warning"
                                onClick={() => handleImageClick(row[4], row[0])}
                              >
                                {loadingImage === row[0] ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </MDButton>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {downloadButton && (
                <MDBox display="flex" justifyContent="center" mt={3}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleDownload}
                    startIcon={loadingZip ? null : <FileDownloadIcon />}
                    disabled={selectedRows.length === 0 || loadingZip}
                  >
                    {loadingZip ? (
                      <>
                        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        Downloading...
                      </>
                    ) : (
                      "Download Zip"
                    )}
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
          )}

          <Modal open={modalVisible} onClose={closeModal}>
            <Box sx={modalStyle}>
              <MDTypography variant="h6" component="h2" mb={2}>
                POD Image for {selectedCNoteNumber}
              </MDTypography>
              {loadingImage === selectedCNoteNumber ? (
                <MDBox display="flex" justifyContent="center" p={3}>
                  <CircularProgress color="info" />
                </MDBox>
              ) : (
                <TransformWrapper>
                  {({ zoomIn, zoomOut, resetTransform }) => (
                    <>
                      <MDBox display="flex" justifyContent="center" mb={2}>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={() => zoomIn()}
                          sx={{ mx: 1 }}
                        >
                          <ZoomInIcon />
                        </MDButton>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={() => zoomOut()}
                          sx={{ mx: 1 }}
                        >
                          <ZoomOutIcon />
                        </MDButton>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={handleRotate}
                          sx={{ mx: 1 }}
                        >
                          <RotateRightIcon />
                        </MDButton>
                        <MDButton
                          variant="gradient"
                          color="error"
                          onClick={() => {
                            resetTransform();
                            handleResetRotation();
                          }}
                          sx={{ mx: 1 }}
                        >
                          Reset
                        </MDButton>
                      </MDBox>
                      <TransformComponent>
                        <Box
                          component="img"
                          src={selectedImage}
                          alt="POD"
                          sx={{
                            transform: `rotate(${rotationAngle}deg)`,
                            height: "350px",
                            width: "100%",
                            objectFit: "contain",
                            display: "block",
                            mx: "auto",
                          }}
                        />
                      </TransformComponent>
                    </>
                  )}
                </TransformWrapper>
              )}
              <MDBox display="flex" justifyContent="flex-end" mt={2}>
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleDownloadImage}
                  startIcon={<FileDownloadIcon />}
                  sx={{ mr: 2 }}
                >
                  Image
                </MDButton>
                <MDButton variant="gradient" color="error" onClick={closeModal}>
                  Close
                </MDButton>
              </MDBox>
            </Box>
          </Modal>

          <Modal open={errorModal} onClose={() => setErrorModal(false)}>
            <Box sx={modalStyle}>
              <MDTypography variant="h6" component="h2" mb={2}>
                Error
              </MDTypography>
              <MDBox display="flex" alignItems="center">
                <ErrorIcon color="error" sx={{ mr: 1 }} />
                <MDTypography>From Date cannot be greater than To Date</MDTypography>
              </MDBox>
            </Box>
          </Modal>

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default PodDownload;
